/**
 * Grille isométrique Dofus d'une salle ROGFUS : 2 x 2 cartes Retro (15 x 17) = 30 colonnes x 34 doubles-lignes,
 * soit 67 lignes alternées de 30 (lignes paires) et 29 cellules (lignes impaires, décalées d'une demi-cellule).
 * Rangement rectangulaire `row * COLS + col` ; la colonne 29 des lignes impaires n'existe pas.
 * Losange 53 x 27 px au zoom 1, rendu au zoom 3,4 (cohérent avec les atlas de personnages).
 */
export const ZOOM = 3.4;
export const COLS = 30;
export const ROWS = 67;
export const CELL_W = 53 * ZOOM;
export const CELL_H = 27 * ZOOM;
export const ROOM_W = COLS * CELL_W;
export const ROOM_H = 34 * CELL_H;
export const CELLS = ROWS * COLS;

export const idx = (row: number, col: number): number => row * COLS + col;
export const rowCol = (i: number): { row: number; col: number } => ({ row: Math.floor(i / COLS), col: i % COLS });
export const isValid = (row: number, col: number): boolean => row >= 0 && row < ROWS && col >= 0 && col < COLS - (row & 1);
export const isValidIdx = (i: number): boolean => i >= 0 && i < CELLS && isValid(Math.floor(i / COLS), i % COLS);

/** Centre d'une cellule en pixels de la salle (origine en haut à gauche). */
export function cellCenter(row: number, col: number): { x: number; y: number } {
  return { x: col * CELL_W + ((row & 1) ? CELL_W : CELL_W / 2), y: row * CELL_H / 2 + CELL_H / 2 };
}
export const centerOf = (i: number): { x: number; y: number } => cellCenter(Math.floor(i / COLS), i % COLS);

/**
 * Cellule contenant le point (x, y), -1 hors de la grille. Les centres forment le réseau engendré par
 * (W/2, H/2) et (W/2, -H/2) : en coordonnées de ce réseau, chaque losange est un carré unité centré sur un point entier.
 */
export function cellAt(x: number, y: number): number {
  if (x < 0 || y < 0 || x > ROOM_W || y > ROOM_H) return -1;
  const u = (x - CELL_W / 2) / CELL_W, v = (y - CELL_H / 2) / CELL_H;
  const a = Math.round(u + v), b = Math.round(u - v);
  const row = a - b;
  const col = (a + b - (row & 1)) / 2;
  return isValid(row, col) ? idx(row, col) : -1;
}

/** 4 voisins par un côté (diagonales écran) puis 4 voisins par un sommet (haut, bas, gauche, droite). */
export function neighbours(i: number): number[] {
  const row = Math.floor(i / COLS), col = i % COLS;
  const odd = row & 1;
  const cand: [number, number][] = [
    [row - 1, col - 1 + odd], [row - 1, col + odd], [row + 1, col - 1 + odd], [row + 1, col + odd],
    [row - 2, col], [row + 2, col], [row, col - 1], [row, col + 1],
  ];
  const out: number[] = [];
  for (const [r, c] of cand) if (isValid(r, c)) out.push(idx(r, c));
  return out;
}

/** Voisins par un côté seulement (connexité 4 des losanges). */
export function sideNeighbours(i: number): number[] {
  const row = Math.floor(i / COLS), col = i % COLS;
  const odd = row & 1;
  const out: number[] = [];
  for (const [r, c] of [[row - 1, col - 1 + odd], [row - 1, col + odd], [row + 1, col - 1 + odd], [row + 1, col + odd]] as [number, number][]) if (isValid(r, c)) out.push(idx(r, c));
  return out;
}

/** Ensemble de bits de taille fixe (un bit par cellule). */
export class Bitset {
  readonly words: Uint32Array;
  constructor(readonly size: number, words?: Uint32Array) { this.words = words ?? new Uint32Array(Math.ceil(size / 32)); }
  get(i: number): boolean { return i >= 0 && i < this.size && (this.words[i >>> 5]! & (1 << (i & 31))) !== 0; }
  set(i: number): void { this.words[i >>> 5]! |= 1 << (i & 31); }
  clear(i: number): void { this.words[i >>> 5]! &= ~(1 << (i & 31)); }
  count(): number { let n = 0; for (let i = 0; i < this.size; i++) if (this.get(i)) n++; return n; }
  clone(): Bitset { return new Bitset(this.size, this.words.slice()); }
}

/**
 * Distances (en pas de `sideNeighbours`) depuis `start` sur les cellules marchables ; -1 = inaccessible.
 * Connexité par les côtés seulement : un corps de rayon non nul ne passe pas par un simple sommet entre deux obstacles.
 */
export function bfs(walk: Bitset, start: number | number[]): Int16Array {
  const dist = new Int16Array(CELLS).fill(-1);
  const queue = new Int32Array(CELLS);
  let head = 0, tail = 0;
  for (const s of Array.isArray(start) ? start : [start]) if (walk.get(s) && dist[s] === -1) { dist[s] = 0; queue[tail++] = s; }
  while (head < tail) {
    const i = queue[head++]!;
    const d = dist[i]! + 1;
    for (const k of sideNeighbours(i)) if (dist[k] === -1 && walk.get(k)) { dist[k] = d; queue[tail++] = k; }
  }
  return dist;
}
